import {
  parsePhoneNumberFromString,
  getCountries,
  getCountryCallingCode,
  type CountryCode,
} from "libphonenumber-js";

export type PageLocale = "en" | "pt" | "es";

/**
 * Idioma da página pelo primeiro segmento da URL.
 * Mesma lógica que estava no crm.ts; vive aqui para o seletor de telefone
 * e o envio ao CRM usarem a mesma fonte.
 */
export function resolvePageLocale(): PageLocale {
  if (typeof window === "undefined") return "en";
  const firstSegment = window.location.pathname.split("/")[1] ?? "";
  if (firstSegment === "pt" || firstSegment === "es") return firstSegment;
  return "en";
}

/**
 * País pré-selecionado no seletor.
 * /pt  -> Brasil.
 * /es  -> Estados Unidos: a maior parte do público hispânico do escritório
 *         está nos EUA, não fora dele.
 * /en  -> Estados Unidos.
 */
export function defaultCountryForLocale(locale: PageLocale): CountryCode {
  return locale === "pt" ? "BR" : "US";
}

/** Países que aparecem no topo do seletor, na ordem do público do escritório. */
export const PRIORITY_COUNTRIES: CountryCode[] = [
  "US",
  "BR",
  "MX",
  "CO",
  "DO",
  "EC",
  "SV",
  "GT",
  "HN",
  "PE",
  "VE",
  "AR",
  "ES",
  "PT",
];

/**
 * Normaliza para E.164.
 *
 * Devolve "" quando o número não é válido — nunca "+", nunca dígito solto.
 * Um contato sem telefone é recuperável pelo e-mail; um contato com telefone
 * inventado parece válido no CRM e ninguém descobre que não disca.
 *
 * Quando o texto já começa com "+", o código do país vem dele e `country`
 * é ignorado. É o caso de quem digita +55 por conta própria.
 */
export function toE164(raw: string, country?: CountryCode): string {
  const value = (raw ?? "").trim();
  if (!value) return "";

  const parsed = parsePhoneNumberFromString(
    value,
    value.startsWith("+") ? undefined : country
  );

  return parsed && parsed.isValid() ? parsed.number : "";
}

export function isValidPhone(raw: string, country?: CountryCode): boolean {
  return toE164(raw, country) !== "";
}

export interface CountryOption {
  code: CountryCode;
  /** Nome do país no idioma da página. */
  name: string;
  /** Código de discagem, sem o "+". */
  callingCode: string;
}

/**
 * Lista completa de países, com os mais prováveis no topo e o resto em ordem
 * alfabética. Os nomes saem traduzidos pelo Intl.DisplayNames; onde ele não
 * existir, cai para a sigla ISO.
 */
export function countryOptions(displayLocale: string): CountryOption[] {
  let names: Intl.DisplayNames | undefined;
  try {
    names = new Intl.DisplayNames([displayLocale], { type: "region" });
  } catch {
    names = undefined;
  }

  const label = (code: CountryCode): string => {
    try {
      return names?.of(code) ?? code;
    } catch {
      return code;
    }
  };

  const toOption = (code: CountryCode): CountryOption => ({
    code,
    name: label(code),
    callingCode: getCountryCallingCode(code),
  });

  const all = getCountries();
  const priority = PRIORITY_COUNTRIES.filter((code) => all.includes(code));
  const prioritySet = new Set<string>(priority);

  const rest = all
    .filter((code) => !prioritySet.has(code))
    .map(toOption)
    .sort((a, b) => a.name.localeCompare(b.name, displayLocale));

  return [...priority.map(toOption), ...rest];
}
